import React, { useCallback } from 'react';
import Set from './Set';
import Video from './Video';
import LazyImage from '../../../../components/lazyImage';
import ZoomerImageWrapper from './ZoomerImageWrapper';
import { ViewerMediaItem, ImageTransformation, ZoomedImage } from '../../../../types';

interface MediaElementProps {
  index: number;
  media: ViewerMediaItem;
  className?: string;
  recurrentSet?: boolean;
  disableTouchOnSet?: boolean;
  autoplay?: boolean;
  setZoomedImage?: React.Dispatch<React.SetStateAction<ZoomedImage | undefined>>;
}

function MediaElement({
  index,
  media,
  className,
  recurrentSet = false,
  disableTouchOnSet = false,
  autoplay = false,
  setZoomedImage
}: MediaElementProps): React.ReactElement {
  const transformationTemplates = setZoomedImage
    ? { 1: ImageTransformation.LOUPDP_M, 2: ImageTransformation.LOUPDP_M_R }
    : { 1: ImageTransformation.LOUPDP_L, 2: ImageTransformation.LOUPDP_L_R };

  const WrappedImage = useCallback(() => {
    const renderImage = (): React.ReactElement => (
      <LazyImage
        className={`media-gallery__item ${className}__item`}
        src={media.src}
        transformationTemplates={transformationTemplates}
      />
    );

    return setZoomedImage ? (
      <ZoomerImageWrapper index={index} setZoomedImage={setZoomedImage}>
        {renderImage()}
      </ZoomerImageWrapper>
    ) : (
      renderImage()
    );
  }, [setZoomedImage, className, media.src, index]);

  function WrappedSet(): React.ReactElement | null {
    if (!media.items) {
      return null;
    }

    return setZoomedImage ? (
      <ZoomerImageWrapper index={index} setZoomedImage={setZoomedImage}>
        <Set
          set={{ items: media.items }}
          className={`${className}__image-set`}
          autoplay={autoplay}
          transformationTemplates={transformationTemplates}
          showControls={false}
          recurrent
          disableTouch
        />
      </ZoomerImageWrapper>
    ) : (
      <Set
        set={{ items: media.items }}
        className={`${className}__image-set`}
        autoplay={autoplay}
        transformationTemplates={transformationTemplates}
        showControls={false}
        recurrent={recurrentSet}
        disableTouch={disableTouchOnSet}
      />
    );
  }

  function WrappedVideo(): React.ReactElement {
    const renderVideo = (): React.ReactElement => <Video src={media.src} className={`${className}__video`} />;

    return setZoomedImage ? (
      <ZoomerImageWrapper index={index} setZoomedImage={setZoomedImage}>
        {renderVideo()}
      </ZoomerImageWrapper>
    ) : (
      renderVideo()
    );
  }

  const elementTypes = {
    img: (): React.ReactElement => <WrappedImage />,
    set: (): React.ReactElement => <WrappedSet />,
    video: (): React.ReactElement => <WrappedVideo />
  };

  return <>{elementTypes[media.type]()}</>;
}

export default MediaElement;
